import { create } from "zustand";
import { getApiUrl } from "@/utils/apiConfig";

export interface NewsletterSubscriber {
  id: string;
  email: string;
  date: string;
}

interface NewsletterStore {
  subscribers: NewsletterSubscriber[];
  isLoaded: boolean;
  fetchSubscribers: () => Promise<void>;
  addSubscriber: (email: string) => Promise<boolean>;
  removeSubscriber: (id: string) => Promise<void>;
}

const API_URL = getApiUrl();

export const useNewsletterStore = create<NewsletterStore>((set, get) => ({
  subscribers: [],
  isLoaded: false,

  fetchSubscribers: async () => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const res = await fetch(`${API_URL}/newsletter.php?t=${Date.now()}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) throw new Error("API Error");
      const data = await res.json();
      if (Array.isArray(data)) {
        set({ subscribers: data, isLoaded: true });
      }
    } catch (e) {
      console.error("Failed to fetch newsletter subscribers", e);
    }
  },

  addSubscriber: async (email) => {
    const { subscribers } = get();
    if (subscribers.some(sub => sub.email.toLowerCase() === email.toLowerCase())) {
      return false; // Already exists locally
    }
    
    const newSubscriber: NewsletterSubscriber = {
      id: Math.random().toString(36).substring(2, 9),
      email,
      date: new Date().toISOString()
    };
    
    // Optimistic update
    set({ subscribers: [newSubscriber, ...subscribers] });

    try {
      const res = await fetch(`${API_URL}/newsletter.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'subscribe', email, id: newSubscriber.id, date: newSubscriber.date })
      });
      
      if (!res.ok) {
        // Revert on failure
        set({ subscribers: get().subscribers.filter(s => s.id !== newSubscriber.id) });
        return false;
      }
      return true;
    } catch (e) {
      console.error("Failed to add newsletter subscriber", e);
      set({ subscribers: get().subscribers.filter(s => s.id !== newSubscriber.id) });
      return false;
    }
  },

  removeSubscriber: async (id) => {
    const { subscribers } = get();
    // Optimistic update
    set({ subscribers: subscribers.filter(s => s.id !== id) });

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      await fetch(`${API_URL}/newsletter.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action: 'remove', id })
      });
    } catch (e) {
      console.error("Failed to remove newsletter subscriber", e);
      // Fallback: refetch state if something goes wrong
      get().fetchSubscribers();
    }
  }
}));

// Fallbacks de rétrocompatibilité pour éviter de casser des fichiers non migrés au besoin
export const getNewsletterSubscribers = () => useNewsletterStore.getState().subscribers;
export const addNewsletterSubscriber = (email: string) => useNewsletterStore.getState().addSubscriber(email);
export const removeNewsletterSubscriber = (id: string) => useNewsletterStore.getState().removeSubscriber(id);
